import React, { FunctionComponent, useEffect, useReducer } from "react";
import { SectionHeading, ContentSection, Paragraph } from "@vwfs-bronson/bronson-react";
import "./CheckoutDacPage.css";
import { initializeWizard } from "./DacWizard";
import { Output } from "../../Text";
import { ProductModifier } from "@busdk/storefront-service/dist/models/enums";
import * as H from 'history';
import { CreditCheckPopup } from '../../components/CreditCheckPopup/CreditCheckPopup';
import { DeepPartial } from '../../common/types/util';
import { performDacSoftCreditCheck, redirectBasedOnCreditCheckResult } from '../../services/api/softCreditCheck';
import { initializeDacSession } from '../../services/api/digitalAccountCheck/digitalAccountCheck';
import { Analytics } from '../../services/analytics';
import { setActualPage, trackAbort } from '../page.utils';

export interface DacPageState {
  transactionId: string;
  sessionKey: string;
  digitalAccountCheckTransactionId: string;
  error: boolean;
  isSessionInitializationInProgress: boolean;
  isCreditCheckInProgress: boolean;
  isCreditCheckApproved: boolean;
  productModifier: ProductModifier.USED | ProductModifier.NEW;
}

export const CreateDacPageActions = (
  getState: () => DacPageState,
  /** expect state to be merged with existing state */
  setState: (state: DeepPartial<DacPageState>) => void
) => {
  return {
    initializeDacSessionIfNone: async (transactionId:string, history: H.History, onError: (error: Error) => void) => {
      const { isSessionInitializationInProgress, sessionKey } = getState();
      if (isSessionInitializationInProgress || sessionKey) {
        return;
      }
      try {
        setState({ isSessionInitializationInProgress: true });
        const result = await initializeDacSession(transactionId);
        setState({
          sessionKey: result.wizardSessionKey,
          digitalAccountCheckTransactionId:
            result.digitalAccountCheckTransactionId,
        });
      } catch (error: any) {
        onError && onError(error);
        setState({ error: true });
      } finally {
        setState({ isSessionInitializationInProgress: false });
      }
    },
    loadDacCreditResult: async (history: H.History, onError: (error: Error) => void) => {
      const {
        isCreditCheckInProgress,
        transactionId,
        productModifier,
        digitalAccountCheckTransactionId,
      } = getState();
      if (isCreditCheckInProgress) return;
      try {
        setState({ isCreditCheckInProgress: true });
        const creditCheckResult = await performDacSoftCreditCheck({
          transactionId,
          productmodifier: productModifier,
          digitalAccountCheckTransactionId: digitalAccountCheckTransactionId,
        });
        setState({
          isCreditCheckApproved: creditCheckResult.isEligible,
        });
        // redirect to approve or refused page depending on credit check result
        redirectBasedOnCreditCheckResult(history, creditCheckResult);
      } catch (e: any) {
        onError && onError(e);
        setState({ error: true });
      } finally {
        setState({ isCreditCheckInProgress: false });
      }
    },
  };
};

export type CheckoutDacPageProps = {
  transactionId: string;
  productModifier: ProductModifier.USED | ProductModifier.NEW;
  history: H.History;
  onError: (error: Error) => void;
};

export const CheckoutDacPage: FunctionComponent<CheckoutDacPageProps> = ({
  transactionId,
  productModifier,
  history,
  onError,
}) => {
  const dacStateReducer = (
    state: DacPageState,
    action: Partial<DacPageState>
  ) => {
    return {
      ...state,
      ...action,
      productModifier,
    };
  };

  const [dacState, patchDacState] = useReducer(dacStateReducer, {
    transactionId: transactionId,
  } as DacPageState);
  const {
    initializeDacSessionIfNone,
    loadDacCreditResult,
  } = CreateDacPageActions(() => dacState, patchDacState);

  initializeDacSessionIfNone(transactionId, history, onError);

  useEffect(() => {
    // initialize the DAC wizard only after sessionKey is created
    if (dacState.sessionKey) {
      initializeWizard(() => {
        loadDacCreditResult(history, onError);
      }, history);
    }
  }, [dacState.sessionKey, history, transactionId, loadDacCreditResult, onError]);

  const pageName = 'Bank selection (DAC)'

  const defaultTrackingData = {
    [Analytics.keys.pageName()]: pageName,
    [Analytics.keys.viewChange()]: pageName,
    [Analytics.keys.formType()]: 'Bank data',
    [Analytics.keys.formName()]: 'Bank data'
  }

  const trackPage = () => {
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.eventType(0)]: 'pageView',
        [Analytics.keys.eventAction(0)]: 'Success',
      }
      Analytics.setTrackingData({...defaultTrackingData, ...pageData})
      Analytics.trackPage()
    }, 1000)
  }

  const onUnload = () => {
    trackAbort('', defaultTrackingData)
  }

  const ownUnload = () => {
    window.addEventListener('unload', onUnload)
  }

  const desubscribeUnload = () => {
    window.addEventListener('unload', onUnload)
  }

  useEffect(() => {
    setActualPage(pageName)
    trackPage()
    ownUnload()
    return () => {
      desubscribeUnload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ContentSection pageWrap pageWrapSize="medium">
      <SectionHeading center level="1" smallSpacing={true} primary>
        {Output.resolve("pages.leasing.dac.title")}
      </SectionHeading>
      <div className="o-component-wrapper">
        <Paragraph className="u-text-center">{Output.resolve("pages.leasing.dac.subtitle1")}</Paragraph>
        <Paragraph className="u-text-center">{Output.resolve("pages.leasing.dac.subtitle2")}</Paragraph>
        <div className="dac-instruction u-mb o-page-wrap o-page-wrap--medium">
          <ol className="c-instruction-stepper  c-instruction-stepper--numbered">
            {(Output.resolve("pages.leasing.dac.dacInstruction") as Array<{
              title: string;
              description: string;
            }>).map((step, i) => (
              <li key={step.title} className="c-instruction-stepper__step u-mh">
                <figure className="c-instruction-stepper__item"></figure>
                <div className="c-instruction-stepper__detail">
                  <span className="c-instruction-stepper__title">
                    {step.title}
                  </span>
                  <span className="c-instruction-stepper__description">
                    {step.description}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {dacState.sessionKey && (
          <>
            <section className="o-content-section">
              <div className="o-page-wrap o-page-wrap--medium">
                <Paragraph ><span dangerouslySetInnerHTML={{__html: Output.resolve("pages.leasing.dac.dacDisclaimer")}}/></Paragraph>
                <div
                  id="XS2A-Form"
                  data-xs2a={dacState.sessionKey}
                  className="c-form-section"
                />
              </div>
            </section>
          </>
        )}

        <div className="o-page-wrap o-page-wrap--medium dac-disclaimer c-item-teaser--inline u-mt-small">
          <i className="shield-icon c-item-teaser__icon c-icon c-icon--[semantic-guarantee] u-mt-xsmall" />
          <div className="c-item-teaser__body">
            <ul className="c-ul">
              {(Output.resolve("pages.leasing.dac.dacFeatures") as Array<
                string
              >).map((feature, i) => (
                <li key={i} className="u-m-xxsmall">
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <CreditCheckPopup show={dacState.isCreditCheckInProgress} />
      </div>
    </ContentSection>
  );
};

export default CheckoutDacPage;
